import { useEffect } from "react";

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = "Privacy Policy | IA4Solutions";

    const description =
      "Read the IA4Solutions Privacy Policy to understand how information submitted through our website may be collected, used, protected, and managed.";

    let metaDescription = document.querySelector(
      'meta[name="description"]'
    );

    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.setAttribute("name", "description");
      document.head.appendChild(metaDescription);
    }

    metaDescription.setAttribute("content", description);

    return () => {
      document.title = "IA4Solutions";
    };
  }, []);

  return (
    <main className="min-h-screen bg-white dark:bg-gray-950">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-gray-200 dark:border-gray-800">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl"
        />

        <div className="container relative mx-auto px-4 py-16 sm:px-6 md:py-20 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-blue-700 dark:border-blue-900 dark:bg-blue-950/50 dark:text-blue-300">
              Legal Information
            </span>

            <h1 className="mt-5 text-3xl font-bold leading-tight text-gray-900 dark:text-white sm:text-4xl md:text-5xl">
              Privacy Policy
            </h1>

            <p className="mx-auto mt-5 max-w-3xl text-sm leading-7 text-gray-600 dark:text-gray-300 sm:text-base">
              Your privacy matters to us. This Privacy Policy explains how
              IA4Solutions may collect, use, protect, and manage information
              provided through this website.
            </p>

            <p className="mt-4 text-xs font-medium text-gray-500 dark:text-gray-400">
              Last updated: September 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="container mx-auto px-4 py-12 sm:px-6 md:py-16 lg:px-8">
        <div className="mx-auto max-w-4xl space-y-10">

          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              1. Introduction
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              IA4Solutions is committed to respecting your privacy when you
              visit our website, explore our training programs, contact us,
              register for services, or otherwise interact with our online
              services.
            </p>

            <p className="mt-3 leading-7 text-gray-600 dark:text-gray-300">
              By using this website, you acknowledge that information may be
              processed as described in this Privacy Policy, subject to
              applicable law.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              2. Information We May Collect
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              Depending on the features you use, we may collect information
              that you voluntarily provide to us, including:
            </p>

            <ul className="mt-4 list-disc space-y-2 pl-6 leading-7 text-gray-600 dark:text-gray-300">
              <li>Name and contact information.</li>
              <li>Email address and phone number.</li>
              <li>Course, training, internship, or service interests.</li>
              <li>Information submitted through enquiry, registration, or contact forms.</li>
              <li>Messages or other information you choose to send to us.</li>
            </ul>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              We may also receive limited technical information associated
              with your visit, such as browser type, device information, and
              general website usage data, where applicable.
            </p>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              3. How We Use Information
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              Information may be used to:
            </p>

            <ul className="mt-4 list-disc space-y-2 pl-6 leading-7 text-gray-600 dark:text-gray-300">
              <li>Respond to enquiries and requests.</li>
              <li>Provide information about courses, training, internships, and services.</li>
              <li>Process registrations or other requests you submit.</li>
              <li>Communicate with you about relevant services or updates.</li>
              <li>Improve our website, content, services, and user experience.</li>
              <li>Protect the website and prevent misuse or unauthorized activity.</li>
              <li>Comply with applicable legal or regulatory requirements.</li>
            </ul>
          </section>

          {/* Communications */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              4. Communications
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              If you submit your contact information or subscribe to updates,
              we may use the information you provide to respond to your
              request or send relevant information. You can contact us if you
              no longer wish to receive certain communications.
            </p>
          </section>

          {/* Cookies and Analytics */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              5. Cookies and Analytics
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              Our website may use cookies, analytics tools, or similar
              technologies to understand website usage, improve performance,
              measure traffic, and enhance the user experience.
            </p>

            <p className="mt-3 leading-7 text-gray-600 dark:text-gray-300">
              Depending on your browser and applicable settings, you may be
              able to control or restrict cookies. Some website features may
              not function as intended if certain technologies are disabled.
            </p>
          </section>

          {/* Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              6. How Information May Be Shared
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              We do not intend to sell personal information submitted through
              this website. Information may be shared when reasonably
              necessary to provide a requested service, operate the website,
              work with service providers, protect our rights, prevent fraud
              or misuse, or comply with applicable law.
            </p>
          </section>

          {/* Third Party Services */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              7. Third-Party Services and Links
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              Our website may contain links to or integrations with
              third-party platforms, including social media, video platforms,
              payment providers, analytics services, or other external
              websites.
            </p>

            <p className="mt-3 leading-7 text-gray-600 dark:text-gray-300">
              Third-party websites operate under their own privacy policies
              and terms. We recommend reviewing those policies before
              providing information directly to third parties.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              8. Data Security
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              We take reasonable measures to protect information against
              unauthorized access, misuse, alteration, disclosure, or loss.
              However, no method of transmission or electronic storage can be
              guaranteed to be completely secure.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              9. Data Retention
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              Information may be retained for as long as reasonably necessary
              to fulfil the purposes for which it was collected, maintain
              business records, resolve disputes, enforce agreements, or
              comply with applicable legal requirements.
            </p>
          </section>

          {/* User Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              10. Your Privacy Rights
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              Depending on applicable law, you may have rights relating to your
              personal information, including rights to request access,
              correction, deletion, or information about how your data is
              handled.
            </p>

            <p className="mt-3 leading-7 text-gray-600 dark:text-gray-300">
              To make a privacy-related request, please contact us using the
              contact details provided below.
            </p>
          </section>

          {/* Children */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              11. Children&apos;s Privacy
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              Our website is not intended to knowingly collect personal
              information from children in circumstances where such collection
              is prohibited by applicable law. Where appropriate, users should
              provide information with the involvement of a parent or
              responsible adult.
            </p>
          </section>

          {/* Policy Updates */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              12. Changes to This Privacy Policy
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              We may update this Privacy Policy from time to time to reflect
              changes to our website, services, technology, or legal
              requirements. Updated versions will be published on this page
              with a revised date where appropriate.
            </p>
          </section>

          {/* Contact */}
          <section className="rounded-2xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-800 dark:bg-gray-900/60">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              13. Contact Us
            </h2>

            <p className="mt-4 leading-7 text-gray-600 dark:text-gray-300">
              For privacy-related questions, requests, or concerns, contact
              IA4Solutions:
            </p>

            <div className="mt-4 space-y-2 text-sm leading-6 text-gray-700 dark:text-gray-300">
              <p>
                <strong>Email:</strong>{" "}
                <a
                  href="mailto:hr@ia4solutions.com"
                  className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400"
                >
                  hr@ia4solutions.com
                </a>
              </p>

              <p>
                <strong>Phone:</strong> 8332029863 | 8008638374
              </p>

              <p>
                <strong>Address:</strong> #23-7-168, Santhi Nagar, M.R.Palli
                Circle, Tirupati – 517502
              </p>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicy;